import React, { useEffect, useState } from "react";
import instance from "../../../axios/instance";
import { getToken } from "../../../utils/token";
import {
  Link,
  Outlet,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Profile, Refresh2, SearchNormal } from "iconsax-react";
import { timeAgo } from "../../../utils/calculateAge";
import { NoAccessInfo } from "../../../components/NoAccessInfo";
import { TextMessageItem } from "../../../components/modals/order/TextMessageItem";

export const messagesLoader = async ({ params }) => {
  const token = getToken();
  const messages = await instance.get("/doctor/messages", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const res = await instance.get("/doctor/billing/plan/active", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const patients = await instance.get("/doctor/patient/all", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return {
    messages: messages.data,
    plan: res.data,
    patients: patients.data
      ? patients.data.map((_) => ({
          ..._,
          label: `${_.lastname} ${_.firstname} - Phone: ${_.cell_phone} - ID: ${_.id}`,
          value: _.cell_phone,
          id: _.id,
        }))
      : [],
  };
};

export const MessagesPage = () => {
  const { messages, plan, patients } = useLoaderData();
  const [filteredList, setFilteredList] = useState(messages);
  const [searchQuery, setSearchQuery] = useState("");
  const [textMessageItem, setTextMessageItem] = useState(null);
  const noAccessToMessages = plan.noAccess.includes("in-app-chat");
  const revalidator = useRevalidator();
  const navigate = useNavigate();

  useEffect(() => {
    setFilteredList(messages);
  }, [messages]);

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredList(() => {
      return messages.filter((messages) => {
        return (
          messages.msg.toLowerCase().includes(query) ||
          messages.order_id.toLowerCase().includes(query)
        );
      });
    });
  };

  return (
    <div className="bg-[#fafafa] max-w-[100%] w-full h-screen">
      <div className="flex justify-between items-center px-7 py-5">
        <div className="text-2xl font-bold">Messages</div>
      </div>

      {noAccessToMessages ? (
        <div className="bg-white -mb-5">
          <NoAccessInfo
            msg={`Your current plan (${plan.name}) does not include the In-app Chats`}
          />
        </div>
      ) : (
        <div className="grid grid-cols-[auto_350px] gap-5 px-7 h-[90vh]">
          <div className="border rounded-lg py-4 bg-white h-[90vh] relative">
            <div className="flex justify-between items-center px-4 pb-3 border-b">
              <div className="flex gap-x-3 items-center">
                <h2>Text Messages ({messages.length})</h2>
                <Refresh2
                  onClick={() => revalidator.revalidate()}
                  size={18}
                  className="text-gray-600 cursor-pointer hover:-rotate-90 duration-300"
                />
              </div>
              <div>
                <button
                  className="bg-primary py-2 px-3 shadow-sm rounded-md font-medium text-[14px] text-white"
                  onClick={() => setTextMessageItem(true)}
                >
                  New Message +
                </button>
              </div>
            </div>
            <form className="relative py-3 px-4" onSubmit={handleSearch}>
              <input
                className="outline-none h-[40px] w-full border rounded-md pl-10 p-2 placeholder:font-normal placeholder:text-[14px] text-sm"
                placeholder="Search anything"
                name="query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 left-[20px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
                type="submit"
              >
                <SearchNormal size={15} />
              </button>
            </form>
            {textMessageItem && (
              <TextMessageItem
                onClose={() => setTextMessageItem(null)}
                dbMessages={[]}
                isNewMessage={textMessageItem === true}
                dbPatients={patients}
              />
            )}
          </div>
          <div className="border rounded-lg py-4 bg-white h-full">
            <div className="flex justify-between items-center px-4 pb-3 border-b">
              <h2>In-app Messages ({messages.length})</h2>
              <Refresh2
                onClick={() => revalidator.revalidate()}
                size={18}
                className="text-gray-600 cursor-pointer hover:-rotate-90 duration-300"
              />
            </div>
            <form className="relative py-3 px-2" onSubmit={handleSearch}>
              <input
                className="outline-none h-[40px] w-full border rounded-md pl-10 p-2 placeholder:font-normal placeholder:text-[14px] text-sm"
                placeholder="Search anything"
                name="query"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button
                className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 left-[10px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
                type="submit"
              >
                <SearchNormal size={15} />
              </button>
            </form>
            <div className="max-h-[75vh] overflow-y-scroll scrollbar-hide">
              {filteredList.map((message, index) => (
                <div
                  key={index}
                  className={`border-b py-4 $font-normal bg-[#fff] hover:opacity-60 ${
                    message.status === "unread" ? "bg-gray-100" : ""
                  } transition duration-200 ease-in-out cursor-pointer px-4`}
                  onClick={() =>
                    navigate(`/dashboard/messages/order/${message.order_id}`)
                  }
                >
                  <p key={index} className={`text-sm`}>
                    {message.msg} - #{message.order_id}
                  </p>
                  <span className="text-xs text-gray-700">
                    {timeAgo(message.created_at)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <Outlet />
    </div>
  );
};
